import React from 'react';
import { Drawer, Button, Input, Select, Spin } from 'antd';
import { UserRound, Mail, Lock, IdCard, Store, X } from 'lucide-react';
import { IconAction } from '../../common';
import { useUserDrawer, storeMatches } from './userDrawer.controller';

function FormSection({ title, children }) {
    return (
        <section className="space-y-4">
            <h3 className="m-0 text-xs font-semibold uppercase tracking-wide text-surface-500">{title}</h3>
            {children}
        </section>
    );
}

function FormField({ label, required, children }) {
    return (
        <div className="flex flex-col gap-1.5 min-w-0">
            <span className="text-sm font-medium text-surface-700">
                {label} {required && <span className="text-danger-fg">*</span>}
            </span>
            {children}
        </div>
    );
}

const Divider = () => <div className="border-t border-surface-100" />;

const clientFilter = (input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase());

export default function UserDrawer({ isOpen, setIsOpen, selectedUser, setSelectedUser, createOrUpdateUser, contextHolder, clients = [], fetchStoresByClient, fetchStoreById }) {
    const {
        isEdit,
        roleOptions,
        name,
        setName,
        email,
        onEmailChange,
        password,
        setPassword,
        channel,
        setChannel,
        userType,
        onUserTypeChange,
        rif,
        setRif,
        active,
        setActive,
        loading,
        clientId,
        setClientId,
        onRouteClientChange,
        routeStores,
        addRouteStore,
        removeRouteStore,
        storeOptions,
        storesLoading,
        findRouteStore,
        onCloseDrawer,
        onSubmit
    } = useUserDrawer({ isOpen, setIsOpen, selectedUser, setSelectedUser, createOrUpdateUser, fetchStoresByClient, fetchStoreById });

    const clientOptions = clients.map((c) => ({ value: c._id, label: c.name }));

    return (
        <Drawer
            title={
                <div>
                    <div className="text-base font-semibold text-surface-900">{isEdit ? 'Editar Usuario' : 'Crear Usuario'}</div>
                    <div className="text-xs font-normal text-surface-500">
                        {isEdit ? 'Modifica los datos del usuario' : 'Completa los datos para crear un nuevo usuario'}
                    </div>
                </div>
            }
            onClose={() => onCloseDrawer()}
            open={isOpen}
            width={600}
            footer={
                <div className="flex justify-end gap-2">
                    <Button onClick={onCloseDrawer}>Cancelar</Button>
                    <Button type="primary" loading={loading} onClick={() => onSubmit()}>
                        {isEdit ? 'Actualizar Usuario' : 'Crear Usuario'}
                    </Button>
                </div>
            }>
            {contextHolder}
            <div className="space-y-6">
                {/* Personal Information */}
                <FormSection title="Información Personal">
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <FormField label="Nombre" required>
                            <Input
                                placeholder="Ingresa el nombre..."
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                prefix={<UserRound className="text-surface-400" />}
                            />
                        </FormField>
                        <FormField label="RIF">
                            <Input placeholder="Ingresa el RIF..." value={rif} onChange={(e) => setRif(e.target.value)} prefix={<IdCard className="text-surface-400" />} />
                        </FormField>
                    </div>
                </FormSection>

                <Divider />

                {/* Account Information */}
                <FormSection title="Información de Cuenta">
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <FormField label="Correo Electrónico" required>
                            <Input
                                placeholder="correo@ejemplo.com"
                                value={email}
                                onChange={(e) => onEmailChange(e.target.value)}
                                prefix={<Mail className="text-surface-400" />}
                            />
                        </FormField>
                        <FormField label={isEdit ? 'Nueva Contraseña' : 'Contraseña'} required={!isEdit}>
                            <Input.Password
                                placeholder={isEdit ? 'Dejar vacío para mantener' : 'Ingresa la contraseña...'}
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                prefix={<Lock className="text-surface-400" />}
                            />
                        </FormField>
                    </div>
                </FormSection>

                <Divider />

                {/* Role & Channel */}
                <FormSection title="Rol y Canal">
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <FormField label="Tipo de Usuario" required>
                            <Select placeholder="Selecciona un rol" value={userType || undefined} onChange={onUserTypeChange} className="w-full" options={roleOptions} />
                        </FormField>
                        <FormField label="Canal" required>
                            <Select
                                placeholder="Selecciona un canal"
                                value={channel || undefined}
                                onChange={(e) => setChannel(e)}
                                className="w-full"
                                options={[
                                    { value: 'moderno', label: 'Moderno' },
                                    { value: 'tradicional', label: 'Tradicional' }
                                ]}
                            />
                        </FormField>
                    </div>
                </FormSection>

                {/* Client selector (only for client user type) */}
                {userType === 'client' && (
                    <>
                        <Divider />
                        <FormSection title="Empresa Asociada">
                            <FormField label="Cliente" required>
                                <Select
                                    showSearch
                                    placeholder="Selecciona el cliente asociado"
                                    value={clientId || undefined}
                                    onChange={(val) => setClientId(val)}
                                    className="w-full"
                                    filterOption={clientFilter}
                                    options={clientOptions}
                                />
                            </FormField>
                        </FormSection>
                    </>
                )}

                {/* Route section (only for route user type) */}
                {userType === 'route' && (
                    <>
                        <Divider />
                        <FormSection title="Ruta">
                            <FormField label="Cliente" required>
                                <Select
                                    showSearch
                                    placeholder="Selecciona el cliente"
                                    value={clientId || undefined}
                                    onChange={onRouteClientChange}
                                    className="w-full"
                                    filterOption={clientFilter}
                                    options={clientOptions}
                                />
                            </FormField>

                            {clientId && (
                                <FormField label="Agregar Pisos de Venta">
                                    {storesLoading ? (
                                        <div className="flex items-center gap-2 py-2">
                                            <Spin size="small" />
                                            <span className="text-sm text-surface-500">Cargando pisos...</span>
                                        </div>
                                    ) : (
                                        <Select
                                            showSearch
                                            placeholder="Buscar por nombre o RIF y agregar un piso de venta..."
                                            value={undefined}
                                            onChange={addRouteStore}
                                            className="w-full"
                                            filterOption={(input, option) => storeMatches(input, option?.store)}
                                            options={storeOptions}
                                            notFoundContent={<span className="text-sm text-surface-400">Sin pisos disponibles</span>}
                                        />
                                    )}
                                </FormField>
                            )}

                            {routeStores.length > 0 && (
                                <FormField label={`Pisos en Ruta (${routeStores.length})`}>
                                    <div className="max-h-[300px] overflow-y-auto rounded-lg border border-surface-200">
                                        {routeStores.map((storeId) => {
                                            const store = findRouteStore(storeId);
                                            const storeName = store?.name || (storesLoading ? 'Cargando…' : storeId);
                                            const inactive = store && store.active === false;
                                            return (
                                                <div
                                                    key={storeId}
                                                    className="flex items-center justify-between gap-2 border-b border-surface-100 px-3 py-2 last:border-b-0 hover:bg-surface-25">
                                                    <div className="flex min-w-0 items-center gap-2.5">
                                                        <Store className="shrink-0 text-surface-400" />
                                                        <div className="min-w-0">
                                                            <span className="block truncate text-sm text-surface-900" title={storeName}>
                                                                {storeName}
                                                            </span>
                                                            {(store?.rif || store?.clientName || inactive) && (
                                                                <span className="block truncate text-xs text-surface-500">
                                                                    {[store?.rif ? `RIF ${store.rif}` : null, store?.clientName, inactive ? 'Inactivo' : null]
                                                                        .filter(Boolean)
                                                                        .join(' · ')}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <IconAction icon={<X />} label="Quitar de la ruta" danger onClick={() => removeRouteStore(storeId)} />
                                                </div>
                                            );
                                        })}
                                    </div>
                                </FormField>
                            )}

                            {clientId && !storesLoading && routeStores.length === 0 && (
                                <div className="flex items-center gap-2 rounded-lg border border-dashed border-surface-200 bg-surface-50 px-3 py-3">
                                    <Store className="text-surface-300" />
                                    <span className="text-sm text-surface-500">Ningún piso agregado a la ruta aún</span>
                                </div>
                            )}
                        </FormSection>
                    </>
                )}

                {/* Status (only for edit) */}
                {isEdit && (
                    <>
                        <Divider />
                        <FormSection title="Estado">
                            <FormField label="Estado del Usuario" required>
                                <Select
                                    value={active}
                                    onChange={(e) => setActive(e)}
                                    className="w-full"
                                    options={[
                                        { value: true, label: 'Activo' },
                                        { value: false, label: 'Inactivo' }
                                    ]}
                                />
                            </FormField>
                        </FormSection>
                    </>
                )}
            </div>
        </Drawer>
    );
}
